import React, { memo, useMemo, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded'

import { useShallowEqualSelector } from 'custom-hooks'
import { filterIdPrefix } from '../constants'
import DateRangeFilterSection from './DateRangeFilterSection'
import DropdownMenu from 'components/CommonUI/Dropdown'
import PanelIcon from 'components/CommonUI/svgIcons/PanelIcon'
import EquipmentDeviceSelectIcon from 'components/CommonUI/svgIcons/EquipmentDeviceSelectIcon'
import { useEquipmentContext } from '../EquipmentContainer'
import {
  setSelectedDevice,
  setSelectedPanel,
  getFilteringDataSelector,
  getSelectedPanelSelector,
  getSelectedDeviceSelector,
  getIsFetchingFilteringDataSelector,
  setDefaultSelectedPanel,
  setDefaultSelectedDevice,
  clearSelectedFilterData,
} from 'redux/reducers/equipments'

const PageFilter = () => {
  const dispatch = useDispatch()

  const { facilityId } = useEquipmentContext()

  const filterData = useShallowEqualSelector(getFilteringDataSelector)
  const selectedPanel = useShallowEqualSelector(getSelectedPanelSelector)
  const selectedDevice = useShallowEqualSelector(getSelectedDeviceSelector)
  const isFetchingFilterData = useShallowEqualSelector(
    getIsFetchingFilteringDataSelector
  )

  const selectedPanelDefault = useMemo(
    () =>
      filterData?.find((item) => item?.id?.includes(filterIdPrefix.panels)) || {
        label: 'No Panels',
      },
    [filterData]
  )

  const selectedDeviceDefault = useMemo(
    () =>
      selectedPanel?.value?.find((item) =>
        item?.id?.includes(filterIdPrefix.device)
      ) || { label: 'No devices' },
    [selectedPanel?.value]
  )

  const handleSelectPanel = useCallback(
    (selected) => {
      dispatch(setSelectedPanel(selected))
    },
    [dispatch]
  )

  const handleSelectDevice = useCallback(
    (selected) => {
      dispatch(setSelectedDevice(selected))
    },
    [dispatch]
  )

  useEffect(() => {
    dispatch(clearSelectedFilterData())
  }, [dispatch, facilityId])

  useEffect(() => {
    if (filterData.length) {
      dispatch(setDefaultSelectedPanel(selectedPanelDefault))
    }
  }, [filterData, dispatch, selectedPanelDefault])

  useEffect(() => {
    if (selectedPanel.value && selectedPanel.value.length) {
      dispatch(setDefaultSelectedDevice(selectedDeviceDefault))
    }
  }, [dispatch, selectedDeviceDefault, selectedPanel])

  return (
    <div className="equipment_page_filter_container">
      <div className="top_filter_section filter_section">
        <div className="device_select_container">
          <div className="select_wrap">
            <DropdownMenu
              selectedValue={
                isFetchingFilterData
                  ? { label: 'Loading panels...' }
                  : selectedPanel
              }
              controlComponent={(props) => (
                <PanelSelectControl {...props} Icon={PanelIcon} />
              )}
              classNameContainer="equipment_filter_select panel_select"
              disabled={!selectedPanel?.id}
              handleSelectedClick={handleSelectPanel}
            >
              {filterData.map((panel) => {
                return <DropdownMenu.Item key={panel.id} itemData={panel} />
              })}
            </DropdownMenu>
          </div>
          <div className="select_wrap">
            <DropdownMenu
              selectedValue={
                isFetchingFilterData
                  ? { label: 'Loading devices...' }
                  : selectedDevice
              }
              disabled={!selectedDevice?.id}
              classNameContainer="equipment_filter_select device_select"
              controlComponent={(props) => (
                <PanelSelectControl
                  {...props}
                  Icon={EquipmentDeviceSelectIcon}
                />
              )}
              handleSelectedClick={handleSelectDevice}
            >
              {selectedPanel?.value?.map((device) => {
                return <DropdownMenu.Item key={device.id} itemData={device} />
              })}
            </DropdownMenu>
          </div>
        </div>
        <DateRangeFilterSection />
      </div>
    </div>
  )
}

const PanelSelectControl = ({ value, active, className, onClick, Icon }) => {
  const activeClassName = active ? 'active' : ''

  return (
    <span
      className={`equipment_select_panel_control ${className}`}
      onClick={onClick}
    >
      <Icon className="equipment_select_panel_icon" />
      {value}
      <ExpandMoreRoundedIcon
        className={`equipment_select_panel_arrow ${activeClassName}`}
      />
    </span>
  )
}

export default memo(PageFilter)
